import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Business, BusinessBranding } from '../types';
import { useAuth } from '../hooks/useAuth';
import UserProfileDropdown from './UserProfileDropdown';

interface BusinessHeaderProps {
  business: Business;
  branding?: BusinessBranding;
  surfaceColor: string;
  borderColor: string;
  textColor: string;
  mutedColor: string;
  brandColor: string;
  brandSoft?: string;
  brandDark?: string;
  onBookingClick?: () => void;
  showBookNowButton?: boolean;
  showSignInButton?: boolean;
}

const BusinessHeader: React.FC<BusinessHeaderProps> = ({
  business,
  branding,
  surfaceColor,
  borderColor,
  textColor,
  mutedColor,
  brandColor,
  brandSoft,
  brandDark,
  onBookingClick,
  showBookNowButton = true,
  showSignInButton = true
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [logoError, setLogoError] = useState(false);

  // Reset logo error when branding changes
  useEffect(() => {
    setLogoError(false);
  }, [branding?.logoUrl]);

  // Get business initials for logo fallback
  const getBusinessInitials = () => {
    const words = business.name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return business.name.substring(0, 2).toUpperCase();
  };

  const handleBookingClick = () => {
    if (onBookingClick) {
      onBookingClick();
    } else {
      // Default behavior - navigate to booking page
      navigate(`/${business.shortName}/book`);
    }
  };

  const handleSignInClick = () => {
    // Navigate to login page with redirect back to current business
    const currentPath = window.location.pathname;
    navigate(`/login?type=customer&redirect=${encodeURIComponent(currentPath)}`);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Check if website redirect is enabled and website exists
    if (business.websiteRedirectEnabled && business.website && business.website.trim() !== '') {
      // Redirect to external website
      window.location.href = business.website;
    } else {
      // Navigate to internal business page
      navigate(`/${business.shortName}`);
    }
  };

  return (
    <header 
      className="sticky top-0 z-50"
      style={{
        backgroundColor: surfaceColor,
        borderBottom: `1px solid ${borderColor}`
      }}
    >
      <nav className="flex items-center py-3">
        <div className="container mx-auto px-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div 
              onClick={handleLogoClick}
              className="flex items-center gap-2 min-w-0 cursor-pointer"
            >
              {branding?.logoUrl && typeof branding.logoUrl === 'string' && branding.logoUrl.trim() !== '' && !logoError ? (
                <img 
                  src={branding.logoUrl} 
                  alt={`${business.name} Logo`}
                  className="w-12 h-12 rounded-2xl object-cover"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div 
                  className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-xl shadow-md"
                  style={{
                    background: 'conic-gradient(from 180deg at 50% 50%, #facc15, #fde047, #f97316, #facc15)',
                    color: '#92400e'
                  }}
                >
                  {getBusinessInitials()}
                </div>
              )}
              <div className="min-w-0">
                <div 
                  className="font-semibold text-base whitespace-nowrap overflow-hidden text-ellipsis max-w-[260px]"
                  style={{ color: brandColor }}
                >
                  {business.name}
                </div>
                <div 
                  className="text-xs uppercase tracking-wider"
                  style={{ color: mutedColor }}
                >
                  Powered by AACore
                </div>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {showBookNowButton && (
                <button
                  type="button"
                  className="text-sm px-4 py-2 rounded-lg hidden md:inline-flex items-center transition-colors hover:opacity-90"
                  style={{
                    backgroundColor: brandColor,
                    borderColor: brandColor,
                    color: '#fff',
                    boxShadow: `0 8px 18px ${brandColor}4d`
                  }}
                  onClick={handleBookingClick}
                >
                  <i className="bi bi-calendar-check mr-2"></i>
                  Book Now
                </button>
              )}

              {!user && showSignInButton ? (
                <button
                  type="button"
                  className="text-sm px-3 py-1.5 border rounded-lg bg-transparent transition-colors hover:opacity-80"
                  style={{
                    borderColor: `${brandColor}59`,
                    color: brandColor
                  }}
                  onClick={handleSignInClick}
                >
                  <i className="bi bi-person-circle mr-1"></i>
                  <span>Sign in</span>
                </button>
              ) : user ? (
                <UserProfileDropdown 
                  hideName={true}
                  avatarBrandColor={brandColor}
                  loginButtonStyle={{
                    borderColor: `${brandColor}59`,
                    color: brandColor
                  }}
                  loginButtonClassName="text-sm px-3 py-1.5 border rounded-lg bg-transparent transition-colors hover:opacity-80"
                />
              ) : null}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default BusinessHeader;
